// hooks/useStickyContent.js
import { useEffect, useRef, useState, useCallback } from "react";

const useStickyContent = (
  config = {
    minWidth: 900,
    topOffset: 88,
    targetElementId: "article-content",
    hideAtEnd: true,
  }
) => {
  const {
    minWidth = 900,
    topOffset = 88,
    targetElementId = "article-content",
    hideAtEnd = true,
  } = config;

  const stickyRef = useRef(null);
  const [shouldHide, setShouldHide] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  const checkHideCondition = useCallback(() => {
    if (!hideAtEnd || window.innerWidth < minWidth) {
      setShouldHide(false);
      setIsVisible(true);
      return;
    }

    if (!stickyRef.current) return;

    const stickyElement = stickyRef.current;
    const targetElement = document.getElementById(targetElementId);

    if (!targetElement) return;

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const stickyHeight = stickyElement.offsetHeight;

    const targetTop = targetElement.offsetTop;
    const targetBottom = targetTop + targetElement.offsetHeight;

    // Hide when the sticky element would go past the end of target
    const shouldHideElement =
      scrollTop + stickyHeight + topOffset >= targetBottom;

    setShouldHide(shouldHideElement);
    setIsVisible(!shouldHideElement);
  }, [minWidth, topOffset, targetElementId, hideAtEnd]);

  useEffect(() => {
    const handleScroll = () => {
      checkHideCondition();
    };

    const handleResize = () => {
      checkHideCondition();
    };

    // Initial check
    checkHideCondition();

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [checkHideCondition]);

  return {
    stickyRef,
    shouldHide,
    isVisible,
    topOffset,
    minWidth, // Expose for CSS media queries
  };
};

export default useStickyContent;
